/* ============================================================================
   WonderCases — Case Type Switcher (v2.8, index-agnostic + single-case filtering)
   ----------------------------------------------------------------------------
   - Robust "model" extraction (index-agnostic; works if MagSafe has extra options)
   - Filters cards even if the selected model exists in ONLY ONE case (e.g., MagSafe-only)
   - History-friendly navigation (location.replace) + offline queue + tiny toast
   - Exact scroll restore across pages (sessionStorage + TTL) — no layout jump
   - URL normalization: always ?model=..., never ?variant= on the final URL
   - No auto-select if a placeholder ("Please select") is chosen
   - .wc-case-desc is updated from clicked/selected card's data-desc
   - Cross-model matching for combined labels (e.g. "iPhone 12/12 Pro")
   - Idle-time rebuild when sibling JSONs arrive (snappy first paint)
   - Prefetch + Prerender for near-instant case switches (Chrome speculation rules)
   - In-place updates wrapped with a viewport stabilizer (if WC.withStableViewport exists)
   ============================================================================ */

(() => {
  const cache = (window.__wcCaseCache = window.__wcCaseCache || { productJson: {} });

  // Precise scroll keys
  const SCROLL_FLAG   = '__wc_to_switcher';        // legacy fallback
  const SCROLL_POS    = '__wc_prev_scroll';        // exact position key
  const SCROLL_TTL_MS = 15000;                     // 15s validity
  let __wcProg = false;

  /* ---------------- utils ---------------- */
  const normalize = s => String(s || '')
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-]/g, '')
    .replace(/\-+/g, '-')
    .trim();

  // Run heavy work during idle (polyfill)
  const ric = window.requestIdleCallback || function (cb) {
    return setTimeout(() => cb({ timeRemaining: () => 50, didTimeout: true }), 1);
  };

  // Create canonical device keys from a label (BASE-first), tolerant to "12/12 Pro"
  // BASE drops case-type tokens so "iPhone X Slim" ↔ "iPhone X"
  function toKeys(label) {
    if (!label) return [];
    // Basic cleanup + unescape
    let s = String(label)
      .replace(/&amp;/g, '&')
      .replace(/&#x2F;/g, '/')
      .toLowerCase()
      .trim();

    // Normalize separators and remove noise words
    s = s.replace(/\s+and\s+/g, '/').replace(/[,&]+/g, '/');
    s = s.replace(/\bcase(s)?\b/g, '').replace(/\s{2,}/g, ' ').trim();

    // Split combined labels like "iphone 12/12 pro"
    const parts = s.split('/').map(p => p.trim()).filter(Boolean);

    const out = [];
    const slugify = txt => txt.replace(/[^\w ]+/g, '').replace(/\s+/g, '-');

    for (let p of parts) {
      // Ensure brand prefix for consistency if missing
      if (!/^iphone\b/.test(p) && !/^galaxy\b/.test(p) && !/^pixel\b/.test(p) && !/^ipad\b/.test(p) && !/^macbook\b/.test(p)) {
        p = 'iphone ' + p; // default bias for WonderCases catalog
      }

      // BASE: remove case-type tokens to produce canonical model key
      let base = p
        .replace(/\b(mag\s*safe|mag-safe|magsafe)\b/g, '')
        .replace(/\b(slim|tough)\b/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

      // Avoid degenerate "iphone"
      if (!/^(iphone|galaxy|pixel|ipad|macbook)$/.test(base)) {
        const baseSlug = slugify(base);
        if (baseSlug && !out.includes(baseSlug)) out.push(baseSlug); // BASE first
      }

      // FULL: keep original variant label as secondary key (e.g., "iphone-x-slim")
      const fullSlug = slugify(p);
      if (fullSlug && !out.includes(fullSlug)) out.push(fullSlug);
    }

    return out;
  }

  const urlNow = () => new URL(location.href);

  function stripParam(name) {
    const u = urlNow();
    if (u.searchParams.has(name)) {
      u.searchParams.delete(name);
      history.replaceState({}, '', u.pathname + (u.search || ''));
    }
  }

  // One-time URL cleanup on init (drop hash and ?variant remnants)
  (() => {
    if (location.hash || /[?&]variant=/.test(location.search)) {
      const u = new URL(location.href);
      u.hash = '';
      u.searchParams.delete('variant');
      history.replaceState({}, '', u.pathname + (u.search || ''));
    }
  })();

  /* ----- Offline-friendly replace navigation + tiny toast ----- */
  const PENDING_KEY = '__wc_pending_nav';

  function showToast(msg = "You’re offline. We’ll try again when you reconnect.") {
    let el = document.querySelector('.wc-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'wc-toast';
      el.setAttribute('role', 'status');
      Object.assign(el.style, {
        position: 'fixed', left: '50%', bottom: '24px', transform: 'translateX(-50%)',
        padding: '10px 14px', background: 'rgba(17,17,17,0.92)', color: '#fff',
        borderRadius: '10px', fontSize: '14px', zIndex: 9999, transition: 'opacity .25s'
      });
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.style.opacity = '1';
    setTimeout(() => { el.style.opacity = '0'; }, 2400);
  }

  function replaceNavigate(url) {
    if (navigator.onLine === false) {
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ url, ts: Date.now() })); } catch {}
      showToast();
      return;
    }
    try { location.replace(url); } catch { location.href = url; }
  }

  window.addEventListener('online', () => {
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return;
      sessionStorage.removeItem(PENDING_KEY);
      const { url } = JSON.parse(raw) || {};
      if (url) replaceNavigate(url);
    } catch {}
  });

  function hasModelPlaceholderSelected() {
    const sels = Array.from(document.querySelectorAll('select[name^="options["]'));
    return sels.some(sel => {
      const opt = sel.selectedOptions && sel.selectedOptions[0];
      if (!opt) return false;
      const txt = (opt.textContent || '').trim().toLowerCase();
      const val = (opt.value || '').trim().toLowerCase();
      return opt.disabled || val === '' || /select|válassz|kerlek|kérlek|choose/.test(txt);
    });
  }

  async function fetchProductJson(handle) {
    if (!handle) throw new Error('missing handle');
    if (cache.productJson[handle]) return cache.productJson[handle];
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('product json fetch failed: ' + handle);
    const json = await res.json();
    cache.productJson[handle] = json;
    return json;
  }

  // (Legacy heuristic kept for provisional pass; v2.8 does not depend on it)
  function detectModelIndex(jsonByCase) {
    const sample = Object.values(jsonByCase).find(Boolean);
    if (!sample || !Array.isArray(sample.options)) return 0;
    const maxIdx = sample.options.length - 1;
    const BRAND = /(iphone|galaxy|pixel|ipad|note|min|mini|pro|max|ultra)/i;
    let bestIdx = 0, bestScore = -1;

    for (let i = 0; i <= maxIdx; i++) {
      const sets = [];
      const allVals = new Set();
      for (const j of Object.values(jsonByCase)) {
        if (!j) continue;
        const vals = (j.variants || []).map(v =>
          Array.isArray(v.options) ? String(v.options[i] || '') : String(v.title || '')
        );
        const set = new Set(vals.map(x => x.toLowerCase()));
        sets.push(set);
        vals.forEach(v => allVals.add(v));
      }
      let score = 0;
      for (let a = 0; a < sets.length; a++) {
        for (let b = a + 1; b < sets.length; b++) {
          let inter = 0;
          sets[a].forEach(v => { if (sets[b].has(v)) inter++; });
          score += inter * 3;
        }
      }
      const anyBrand = Array.from(allVals).some(v => BRAND.test(v));
      if (anyBrand) score += 10;
      const uniq = new Set(Array.from(allVals).map(v => v.toLowerCase()));
      score += Math.min(uniq.size, 20) * 0.5;
      if (uniq.size <= 1) score -= 5;
      if (score > bestScore) { bestScore = score; bestIdx = i; }
    }
    return bestIdx;
  }

  /* --------- Index-agnostic "model" extraction (NEW) --------- */
  const MODEL_RX = /(iphone|galaxy|pixel|ipad|macbook|note|ultra|pro|max|mini|plus|air)\b/i;

  function extractModelLabelFromVariant(variant) {
    const opts = Array.isArray(variant?.options) ? variant.options : [];

    // 1) look for a typical device token in options
    let found = opts.find(o => MODEL_RX.test(String(o)));
    // 2) if not found, try the variant title split
    if (!found && variant?.title) {
      const parts = String(variant.title).split(' / ');
      found = parts.find(p => MODEL_RX.test(String(p)));
    }
    // 3) fallback: the longest option (often the model)
    if (!found && opts.length) {
      found = opts.slice().sort((a,b) => String(b).length - String(a).length)[0];
    }
    return found ? String(found).trim() : '';
  }

  function modelSlugFromVariant(variant) {
    const label = extractModelLabelFromVariant(variant);
    return label ? normalize(label) : '';
  }

  // Master map indexes the same variant under multiple device keys (BASE + FULL)
  function buildMasterMap(jsonByCase /*, modelIdx not used */) {
    const map = new Map();
    for (const [type, j] of Object.entries(jsonByCase)) {
      if (!j) continue;
      (j.variants || []).forEach(v => {
        if (!v?.available) return;

        const label = extractModelLabelFromVariant(v);
        const keys  = toKeys(label);
        if (!keys.length) return;

        keys.forEach(key => {
          if (!map.has(key)) {
            map.set(key, { availableIn: new Set(), perCaseVariantId: {}, perCaseLabel: {} });
          }
          const e = map.get(key);
          e.availableIn.add(type);
          e.perCaseVariantId[type] = v.id;
          e.perCaseLabel[type]     = label;
        });
      });
    }
    return map;
  }

  function variantIdFromDom() {
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"], input[name="id"]');
    return idInput ? String(idInput.value || '') : '';
  }

  // Derive a canonical key from the selected variant (first key) — index-agnostic
  function slugFromVariantId(json, /* modelIdx unused */ _ignored, fallbackVariantId) {
    if (!json) return '';
    const vid = fallbackVariantId || variantIdFromDom();
    const v = (json.variants || []).find(x => String(x.id) === String(vid));
    if (!v) return '';
    const label = extractModelLabelFromVariant(v);
    const keys  = toKeys(label);
    return keys[0] || '';
  }

  function setVariantId(variantId) {
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"], input[name="id"]');
    if (!idInput) return false;
    const cur = String(idInput.value || '');
    if (cur === String(variantId)) return true;
    __wcProg = true;
    idInput.value = String(variantId);
    idInput.dispatchEvent(new Event('input', { bubbles: true }));
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId } }));
    __wcProg = false;
    return true;
  }

  function setPickersToLabel(label) {
    let changed = false;
    const want = String(label).toLowerCase();
    document.querySelectorAll('select[name^="options["]').forEach(sel => {
      const opt = Array.from(sel.options).find(o => String(o.text || o.value).toLowerCase() === want);
      if (opt) {
        __wcProg = true;
        sel.value = opt.value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        __wcProg = false;
        changed = true;
      }
    });
    document.querySelectorAll('input[type="radio"][name^="options["]').forEach(r => {
      if (String(r.value).toLowerCase() === want && !r.checked) {
        __wcProg = true;
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
        __wcProg = false;
        changed = true;
      }
    });
    return changed;
  }

  /* Cache the switcher cards once to avoid repeated DOM queries */
  function getCards(root) {
    if (root.__cards && Array.isArray(root.__cards)) return root.__cards;
    const list = Array.from(root.querySelectorAll('.wc-case-card'));
    root.__cards = list;
    return list;
  }

  /* Resolve card type robustly: data-case → handle/href suffix → title text */
  function resolveCardType(card) {
    // 1) explicit data-case
    let t = (card.dataset && card.dataset.case ? card.dataset.case : '').toLowerCase();
    if (t) return t;

    // 2) from data-handle or href suffix
    let h = (card.dataset && card.dataset.handle ? card.dataset.handle : '') || card.getAttribute('href') || '';
    h = String(h).toLowerCase();

    // Common WonderCases handle patterns
    if (/-iphone-slim-phone-case/.test(h) || /-slim-case/.test(h))      return 'slim';
    if (/-iphone-tough-phone-case/.test(h) || /-tough-case/.test(h))    return 'tough';
    if (/-iphone-magsafe-phone-case/.test(h) || /-magsafe-case/.test(h)) return 'magsafe';

    // 3) fallback: data-title / inner text
    const title = (card.dataset && card.dataset.title ? card.dataset.title : card.textContent || '').toLowerCase();
    if (title.indexOf('slim') >= 0)  return 'slim';
    if (title.indexOf('tough') >= 0) return 'tough';
    if (title.indexOf('mag') >= 0)   return 'magsafe';

    // Unknown → return empty string (caller will keep it visible)
    return '';
  }

  // Defensive: only hide when we have a valid entry; fail-open otherwise
  function filterCards(root, master, slug, currentCase) {
    const cards = getCards(root);
    const entry = slug ? master.get(slug) : null;
    const shouldFilter = !!(slug && entry);

    let visibleCount = 0;

    for (const card of cards) {
      const type = resolveCardType(card); // slim|tough|magsafe|''

      if (!shouldFilter) {
        card.style.display = '';
      } else {
        if (!type) {
          card.style.display = ''; // unknown type → fail-open
        } else {
          const ok = entry.availableIn && entry.availableIn.has(type);
          card.style.display = ok ? '' : 'none';
        }
      }

      if (card.style.display !== 'none') {
        visibleCount++;
      }

      const isSelected = !!type && (type === currentCase);
      if (isSelected) {
        card.classList.add('is-selected');
        card.setAttribute('aria-current', 'true');
      } else {
        card.classList.remove('is-selected');
        card.setAttribute('aria-current', 'false');
      }
    }

    // Safety: if filtering would hide everything, fall back to showing all
    if (shouldFilter && visibleCount === 0) {
      cards.forEach(card => {
        card.style.display = '';
        const type = resolveCardType(card);
        const isSelected = !!type && (type === currentCase);
        if (isSelected) {
          card.classList.add('is-selected');
          card.setAttribute('aria-current', 'true');
        } else {
          card.classList.remove('is-selected');
          card.setAttribute('aria-current', 'false');
        }
      });
    }
  }

  // Update card HREFs with the current ?model param (no hash, no ?variant)
  function updateCardHrefsWithModel(root, slug) {
    const cards = getCards(root);
    cards.forEach(a => {
      const href = a.getAttribute('href');
      if (!href) return;
      const u = new URL(href, location.origin);
      if (slug) u.searchParams.set('model', slug);
      else u.searchParams.delete('model');
      u.searchParams.delete('variant');
      a.setAttribute('href', u.pathname + (u.search || '')); // path + search only
    });
  }

  /* --- Sibling hero helpers (for image preloading) --- */

  // Build a map: variantId -> hero image URL for a given product JSON
  function buildVariantHeroLookup(json) {
    const map = new Map();
    if (!json || !Array.isArray(json.variants)) return map;

    json.variants.forEach(v => {
      if (!v || !v.id || !v.featured_image) return;
      const idStr = String(v.id);
      const img = v.featured_image;
      const src = img.src || img.url || img; // be tolerant to shape
      if (src && !map.has(idStr)) {
        map.set(idStr, src);
      }
    });

    return map;
  }

  // Attach data-wc-sibling-hero="<hero-url>" to each case card
  // so wc-sibling-hero-preload.js can warm up those hero images.
  function annotateSiblingCardsWithHero(root, master, slug, jsonByCase) {
    if (!root || !master || !slug) return;

    const entry = master.get(slug);
    if (!entry) return;

    const cards = getCards(root);
    if (!cards.length) return;

    // Build per-case hero maps lazily
    const heroMapByCase = {};
    ['slim', 'tough', 'magsafe'].forEach(caseKey => {
      const json = jsonByCase[caseKey];
      if (json) {
        heroMapByCase[caseKey] = buildVariantHeroLookup(json);
      }
    });

    cards.forEach(card => {
      const type = resolveCardType(card); // "slim" | "tough" | "magsafe" | ''
      if (!type) return;

      const variantId =
        entry.perCaseVariantId &&
        entry.perCaseVariantId[type] &&
        String(entry.perCaseVariantId[type]);

      if (!variantId) return;

      const heroMap = heroMapByCase[type];
      if (!heroMap) return;

      const heroSrc = heroMap.get(variantId);
      if (!heroSrc) return;

      card.setAttribute('data-wc-sibling-hero', heroSrc);
    });
  }

  /* --- PRERENDER (Speculation Rules) + PREFETCH helpers --- */

  // Light prefetch as a safe fallback
  function prefetchSiblings(root, slug, limit = 2) {
    try {
      const nc = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (nc && (nc.saveData || (nc.effectiveType && !/4g|wifi/i.test(nc.effectiveType)))) return;

      const head = document.head || document.getElementsByTagName('head')[0];
      if (!head) return;

      const cards = (root.__cards && Array.isArray(root.__cards))
        ? root.__cards
        : Array.from(root.querySelectorAll('a.wc-case-card'));

      let added = 0;
      for (const a of cards) {
        if (added >= limit) break;
        if (!a || a.classList.contains('is-selected') || a.style.display === 'none') continue;

        const href = a.getAttribute('href');
        if (!href) continue;

        const u = new URL(href, location.origin);
        const curModel = new URL(location.href).searchParams.get('model') || slug || '';
        if (curModel) u.searchParams.set('model', curModel);
        u.searchParams.delete('variant');
        const finalHref = u.pathname + (u.search || '');

        if (head.querySelector('link[rel="prefetch"][href="' + finalHref + '"]')) continue;

        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = finalHref;
        link.as = 'document';
        link.setAttribute('data-wc-prefetch', '1');
        head.appendChild(link);
        added++;
      }
    } catch (_) {}
  }

  function wcSupportsSpeculation() {
    try {
      return 'supports' in HTMLScriptElement &&
             typeof HTMLScriptElement.supports === 'function' &&
             HTMLScriptElement.supports('speculationrules');
    } catch (_) { return false; }
  }

  function wcClearSpeculationRules() {
    try {
      document.querySelectorAll('script[type="speculationrules"][data-wc-spec]').forEach(s => s.remove());
    } catch (_) {}
  }

  // Inject a prerender rule for up to N sibling product URLs (fallbacks to prefetch)
  function prerenderSiblings(root, slug, limit = 1) {
    if (!wcSupportsSpeculation()) {
      prefetchSiblings(root, slug, Math.max(1, limit));
      return;
    }
    try {
      const cards = (root.__cards && Array.isArray(root.__cards))
        ? root.__cards
        : Array.from(root.querySelectorAll('a.wc-case-card'));

      const urls = [];
      const curModel = new URL(location.href).searchParams.get('model') || slug || '';

      for (const a of cards) {
        if (urls.length >= limit) break;
        if (!a || a.classList.contains('is-selected') || a.style.display === 'none') continue;

        const href = a.getAttribute('href');
        if (!href) continue;

        const u = new URL(href, location.origin);
        if (u.origin !== location.origin) continue; // same-origin only
        if (curModel) u.searchParams.set('model', curModel);
        u.searchParams.delete('variant');
        urls.push(u.pathname + (u.search || ''));
      }

      if (!urls.length) return;

      wcClearSpeculationRules();
      const s = document.createElement('script');
      s.type = 'speculationrules';
      s.dataset.wcSpec = '1';
      s.text = JSON.stringify({
        prerender: [
          { source: 'list', urls: urls, eagerness: 'moderate' }
        ]
      });
      (document.head || document.documentElement).appendChild(s);
    } catch (_) {}
  }

  // Optional: prerender hovered target ASAP (Chrome-only; safe to keep)
  function prerenderOnHover(root) {
    try {
      if (!wcSupportsSpeculation()) return;
      root.addEventListener('pointerenter', function (ev) {
        const a = ev.target.closest('a.wc-case-card');
        if (!a || a.classList.contains('is-selected') || a.style.display === 'none') return;

        const href = a.getAttribute('href');
        if (!href) return;

        const u = new URL(href, location.origin);
        const curModel = new URL(location.href).searchParams.get('model') || '';
        if (curModel) u.searchParams.set('model', curModel);
        u.searchParams.delete('variant');

        wcClearSpeculationRules();
        const s = document.createElement('script');
        s.type = 'speculationrules';
        s.dataset.wcSpec = '1';
        s.text = JSON.stringify({
          prerender: [
            { source: 'list', urls: [u.pathname + (u.search || '')], eagerness: 'eager' }
          ]
        });
        (document.head || document.documentElement).appendChild(s);
      }, true);

      window.addEventListener('pagehide', wcClearSpeculationRules, { once: true });
    } catch (_) {}
  }

  function updateDescFromSelected(root) {
    const p = root.querySelector('.wc-case-desc');
    if (!p) return;
    const selected = root.querySelector('.wc-case-card.is-selected');
    const desc = selected ? selected.getAttribute('data-desc') : '';
    if (desc) p.textContent = desc;
  }

  function productUrlForHandle(targetHandle) {
    const { pathname } = location;
    const needle = '/products/';
    const idx = pathname.indexOf(needle);
    const base = idx >= 0 ? pathname.slice(0, idx + needle.length) : needle;
    const url = new URL(base + targetHandle, location.origin);
    const cur = new URL(location.href);
    const model = cur.searchParams.get('model');
    if (model) url.searchParams.set('model', model);
    return url;
  }

  // Write the ?model param (no hash, no ?variant)
  function setModelParam(slug) {
    const u = urlNow();
    if (slug) u.searchParams.set('model', slug);
    else u.searchParams.delete('model');
    u.searchParams.delete('variant');
    history.replaceState({}, '', u.pathname + (u.search || '')); // path + search only
  }

  /* ---------- Header offset + scroll restore (cross-page) ---------- */

  function getHeaderOffset() {
    const header =
      document.querySelector('.shopify-section-header') ||
      document.querySelector('.header-wrapper') ||
      document.querySelector('header[role="banner"]') ||
      document.querySelector('header');
    let offset = 0;
    if (header) {
      const cs = getComputedStyle(header);
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        offset = Math.ceil(header.getBoundingClientRect().height || 0);
      }
    }
    return offset;
  }

  function restoreScrollOnce() {
    // (1) Try exact stored position
    try {
      const raw = sessionStorage.getItem(SCROLL_POS);
      if (raw) {
        const data = JSON.parse(raw);
        sessionStorage.removeItem(SCROLL_POS);
        if (data && (Date.now() - (data.ts || 0)) < SCROLL_TTL_MS) {
          const yTarget = Math.max(0, Math.round(+data.y || 0) - getHeaderOffset());
          const prev = document.documentElement.style.scrollBehavior;
          document.documentElement.style.scrollBehavior = 'auto';

          let tries = 0;
          const tryScroll = () => {
            window.scrollTo(0, yTarget);
            tries++;
            const closeEnough = Math.abs(window.pageYOffset - yTarget) <= 2;
            if (closeEnough || tries > 10) {
              document.documentElement.style.scrollBehavior = prev || '';
              return;
            }
            requestAnimationFrame(tryScroll);
          };
          requestAnimationFrame(tryScroll);
          return; // handled / in progress
        }
      }
    } catch (_) {}

    // (2) Legacy: if only the old flag exists, scroll to the switcher
    try {
      if (sessionStorage.getItem(SCROLL_FLAG) === '1') {
        sessionStorage.removeItem(SCROLL_FLAG);
        const root = document.getElementById('wc-case-switcher');
        if (!root) return;
        const y = Math.max(0, root.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
        const prev = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, y);
        document.documentElement.style.scrollBehavior = prev || '';
      }
    } catch (_) {}
  }

  function saveExactScrollY() {
    try {
      const payload = { y: window.pageYOffset, ts: Date.now() };
      sessionStorage.setItem(SCROLL_POS, JSON.stringify(payload));
    } catch (_) {}
  }

  /* ---------- In-place viewport stabilizer wrapper ---------- */

  function stabilize(fn) {
    try {
      const anchor = document.getElementById('wc-case-switcher');
      if (window.WC && typeof window.WC.withStableViewport === 'function' && anchor) {
        window.WC.withStableViewport(anchor, fn, { frames: 14, settleMs: 800 });
      } else {
        fn();
      }
    } catch (_) { fn(); }
  }

  /* ----- CLS guard: lock/unlock list height during filtering ----- */
  function lockListHeight(root) {
    try {
      const list = root.querySelector('.wc-case-list') || root;
      if (!list) return;
      const h = Math.ceil(list.getBoundingClientRect().height || 0);
      if (h > 0) list.style.minHeight = h + 'px';
    } catch (_) {}
  }
  function unlockListHeight(root) {
    try {
      const list = root.querySelector('.wc-case-list') || root;
      if (list) list.style.minHeight = '';
    } catch (_) {}
  }

  /* ---------------- main init ---------------- */
  window.__wcInitCaseSwitcher = async function init() {
    const root = document.getElementById('wc-case-switcher');
    if (!root || root.dataset.inited === '1') {
      restoreScrollOnce();
      return;
    }
    root.dataset.inited = '1';

    // Ensure visible baseline state
    root.classList.add('wc-ready', 'wc-hydrated');

    const meta = {
      currentHandle: root.dataset.currentHandle || '',
      currentCase: (root.dataset.currentCase || 'slim').toLowerCase(),
      designSlug: root.dataset.designSlug || '',
      siblings: {}
    };
    try { meta.siblings = JSON.parse(root.dataset.siblingHandles || '{}'); } catch (_) {}

    // 1) Fetch ONLY the current product first (fast path)
    const jsonByCase = {};
    jsonByCase[meta.currentCase] = await fetchProductJson(meta.currentHandle).catch(() => null);

    // Provisional detection (kept for compatibility; v2.8 is index-agnostic later)
    let modelIdx = detectModelIndex({ [meta.currentCase]: jsonByCase[meta.currentCase] });

    // Provisional master from current only (using index-agnostic builder)
    let master = buildMasterMap({ [meta.currentCase]: jsonByCase[meta.currentCase] });

    // 2) Fetch siblings in background; rebuild master once they arrive
    const siblingTasks = [];
    function queueSibling(caseKey, handle) {
      if (!handle) return;
      siblingTasks.push(
        fetchProductJson(handle)
          .then(j => { jsonByCase[caseKey] = j; })
          .catch(() => {})
      );
    }
    queueSibling('slim',    meta.siblings.slim);
    queueSibling('tough',   meta.siblings.tough);
    queueSibling('magsafe', meta.siblings.magsafe);

    Promise.all(siblingTasks).then(function () {
      ric(function () {
        try {
          const fullMaster = buildMasterMap(jsonByCase); // index-agnostic
          master = fullMaster;

          const currentUrl    = new URL(location.href);
          const effectiveSlug = currentUrl.searchParams.get('model') || '';
          const hasEntry      = effectiveSlug && fullMaster.get(effectiveSlug);
          if (!root || !root.isConnected) return;

          lockListHeight(root);
          stabilize(function () {
            filterCards(root, fullMaster, hasEntry ? effectiveSlug : '', meta.currentCase);
            updateCardHrefsWithModel(root, effectiveSlug);
            prerenderSiblings(root, effectiveSlug, 1);
            updateDescFromSelected(root);
            annotateSiblingCardsWithHero(root, fullMaster, hasEntry ? effectiveSlug : '', jsonByCase);
            setTimeout(function(){ unlockListHeight(root); }, 0);
          });
        } catch (_) {}
      });
    });

    // Expose JSON for debugging
    window.__wcJsonByCase = jsonByCase;

    const u = urlNow();
    let slug = u.searchParams.get('model') || '';
    const placeholderActive = hasModelPlaceholderSelected();

    if (!slug && u.searchParams.has('variant') && !placeholderActive) {
      const derived = slugFromVariantId(jsonByCase[meta.currentCase], modelIdx, u.searchParams.get('variant'));
      if (derived) {
        slug = derived;
        setModelParam(slug);
      }
    }
    if (!slug && !placeholderActive) {
      const derived = slugFromVariantId(jsonByCase[meta.currentCase], modelIdx);
      if (derived) slug = derived;
    }
    if (placeholderActive) {
      stripParam('variant');
    }

    // If selected model not available on this case type, prefer sibling
    if (slug) {
      const e = master.get(slug);
      if (e && !e.availableIn.has(meta.currentCase) && e.availableIn.size > 0) {
        const pref = ['magsafe', 'tough', 'slim'].find(t => e.availableIn.has(t));
        if (pref && pref !== meta.currentCase) {
          const targetHandle = meta.siblings[pref];
          if (targetHandle) {
            saveExactScrollY();
            const go = productUrlForHandle(targetHandle);
            go.searchParams.set('model', slug);
            replaceNavigate(go.toString());
            return;
          }
        }
      }
    }

    // ----- FIRST RENDER -----
    stabilize(() => {
      // First paint: show all (snappy), then refine once siblings arrive
      filterCards(root, master, '', meta.currentCase);
      updateCardHrefsWithModel(root, slug);
      prerenderSiblings(root, slug, 1);
      updateDescFromSelected(root);
      annotateSiblingCardsWithHero(root, master, slug, jsonByCase);

      if (slug) {
        const e = master.get(slug);
        const variantId = e && e.perCaseVariantId && e.perCaseVariantId[meta.currentCase];
        if (variantId) {
          const curLabel = (e && e.perCaseLabel && e.perCaseLabel[meta.currentCase]) || '';
          if (curLabel) setPickersToLabel(curLabel);
          setVariantId(variantId);
        }
        stripParam('variant');
      }
    });

    root.classList.add('wc-hydrated', 'wc-ready');
    prerenderOnHover(root);

    function onAnyOptionChange() {
      if (__wcProg) return;

      // Placeholder selected → clear state
      if (hasModelPlaceholderSelected()) {
        slug = '';
        setModelParam('');
        stabilize(() => {
          filterCards(root, master, '', meta.currentCase);
          updateCardHrefsWithModel(root, '');
          updateDescFromSelected(root);
          annotateSiblingCardsWithHero(root, master, '', jsonByCase);
        });
        stripParam('variant');
        return;
      }

      // Real model selected → derive slug from current product (index-agnostic)
      const curJson = jsonByCase[meta.currentCase];
      const s = slugFromVariantId(curJson, modelIdx);
      if (!s) return;
      slug = s;

      setModelParam(slug);

      // We filter as soon as we have an entry (even if only ONE case has it)
      const siblingsLoaded = ['slim', 'tough', 'magsafe'].some(k => {
        return k !== meta.currentCase && meta.siblings && meta.siblings[k] && jsonByCase[k];
      });
      const entry = slug ? master.get(slug) : null;

      // Data not ready or no entry → keep visible; just update URLs/desc
      if (!siblingsLoaded || !entry) {
        stabilize(function () {
          filterCards(root, master, '', meta.currentCase); // fail-open
          updateCardHrefsWithModel(root, slug);
          prerenderSiblings(root, slug, 1);
          updateDescFromSelected(root);
          annotateSiblingCardsWithHero(root, master, '', jsonByCase);
        });
        stripParam('variant');
        return;
      }

      // Entry exists → FILTER (hide Slim/Tough if model is MagSafe-only, etc.)
      lockListHeight(root);
      stabilize(function () {
        filterCards(root, master, slug, meta.currentCase);
        updateCardHrefsWithModel(root, slug);
        prerenderSiblings(root, slug, 1);
        updateDescFromSelected(root);
        annotateSiblingCardsWithHero(root, master, slug, jsonByCase);

        const vid = entry?.perCaseVariantId?.[meta.currentCase];
        if (vid) {
          const curLabel = entry?.perCaseLabel?.[meta.currentCase] || '';
          if (curLabel) setPickersToLabel(curLabel);
          setVariantId(vid);
        }
        setTimeout(() => { unlockListHeight(root); }, 0);
      });
      stripParam('variant');
    }

    // Observe option controls
    document
      .querySelectorAll('select[name^="options["], input[type="radio"][name^="options["]')
      .forEach(el => el.addEventListener('change', onAnyOptionChange));

    // Track id input changes
    {
      const idInput = document.querySelector('form[action*="/cart/add"] [name="id"], input[name="id"]');
      if (idInput) {
        let last = String(idInput.value || '');
        const onIdChange = () => {
          const cur = String(idInput.value || '');
          if (cur === last) return;
          last = cur;
          onAnyOptionChange();
        };
        try {
          const obs = new MutationObserver(onIdChange);
          obs.observe(idInput, { attributes: true, attributeFilter: ['value'] });
        } catch (_) {}
        idInput.addEventListener('change', onIdChange);
        idInput.addEventListener('input', onIdChange);
      }
    }

    // Card click: store scroll, carry ?model, and ADD a temporary ?variant for SSR
    root.addEventListener('click', (ev) => {
      const a = ev.target.closest('a.wc-case-card');
      if (!a || a.style.display === 'none') return;

      const href = a.getAttribute('href');
      if (!href) return;

      // Update description immediately for perceived speed
      const clickedDesc = a.getAttribute('data-desc');
      if (clickedDesc) {
        const p = root.querySelector('.wc-case-desc');
        if (p) p.textContent = clickedDesc;
      }

      ev.preventDefault();

      const u2 = new URL(href, location.origin);

      // Carry the currently selected model slug forward
      const curModel = (urlNow().searchParams.get('model') || '').trim();
      if (curModel) u2.searchParams.set('model', curModel);

      // Add a temporary ?variant so next page server-renders the correct variant
      try {
        const type = resolveCardType(a); // "slim" | "tough" | "magsafe"
        if (type && curModel && master) {
          const entry = master.get(curModel);
          const vid = entry && entry.perCaseVariantId && entry.perCaseVariantId[type];
          if (vid) {
            u2.searchParams.set('variant', String(vid));
          }
        }
      } catch (_) {
        // Fail-open: still navigate with ?model only
      }

      // Do NOT remove ?variant here; target page will clean it after hydration
      saveExactScrollY();
      setTimeout(() => { replaceNavigate(u2.toString()); }, 0);
    }, true);
  };

  document.addEventListener('DOMContentLoaded', () => {
    window.__wcInitCaseSwitcher && window.__wcInitCaseSwitcher();
    restoreScrollOnce();
  });

  // bfcache-friendly: ensure scroll restore on back/forward cache
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      restoreScrollOnce();
    }
  });
})();


/* ============================================================================
   WC Case Switcher — re-init stabilizer
   - Re-runs init if the switcher DOM is replaced/re-rendered
   - Safe: does nothing if already initialized and ready
   ============================================================================ */
(() => {
  let __wcInitRaf = 0;

  function runCaseSwitcherInit() {
    const root = document.getElementById('wc-case-switcher');
    if (!root) return;

    const alreadyReady =
      root.dataset.inited === '1' &&
      root.classList.contains('wc-ready') &&
      root.classList.contains('wc-hydrated');

    if (alreadyReady) return;

    if (typeof window.__wcInitCaseSwitcher === 'function') {
      window.__wcInitCaseSwitcher();
    }
  }

  function scheduleCaseSwitcherInit() {
    cancelAnimationFrame(__wcInitRaf);
    __wcInitRaf = requestAnimationFrame(runCaseSwitcherInit);
  }

  document.addEventListener('DOMContentLoaded', scheduleCaseSwitcherInit);
  window.addEventListener('pageshow', scheduleCaseSwitcherInit);

  /* Shopify editor / section refresh hooks */
  document.addEventListener('shopify:section:load', scheduleCaseSwitcherInit);
  document.addEventListener('shopify:section:reorder', scheduleCaseSwitcherInit);
  document.addEventListener('shopify:block:select', scheduleCaseSwitcherInit);

  /* DOM replacement guard */
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!node || node.nodeType !== 1) continue;

        if (
          node.id === 'wc-case-switcher' ||
          (node.querySelector && node.querySelector('#wc-case-switcher'))
        ) {
          scheduleCaseSwitcherInit();
          return;
        }
      }
    }

    const root = document.getElementById('wc-case-switcher');
    if (
      root &&
      (
        root.dataset.inited !== '1' ||
        !root.classList.contains('wc-ready') ||
        !root.classList.contains('wc-hydrated')
      )
    ) {
      scheduleCaseSwitcherInit();
    }
  });

  mo.observe(document.body, { childList: true, subtree: true });
})();